import { useState } from "react";
import { KeyRound, LockKeyhole, ShieldAlert, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Loading from "@/components/Loading";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { MfaCard } from "./MfaCard";
import {
  challengePasskey,
  isPasskeySupported,
  verifyTotp,
  type MfaFactor,
} from "../mfa";

const TOTP_CODE_LENGTH = 6;

const errorMessage = (e: unknown, fallback: string) =>
  e instanceof Error && e.message ? e.message : fallback;

/**
 * Challenge for a user who already has a verified second factor but whose
 * current session hasn't completed it. On success the second factor is
 * verified for the session.
 */
export const MfaChallenge = ({
  factors,
  onComplete,
}: {
  factors: MfaFactor[];
  onComplete: () => void;
}) => {
  const passkeyFactor = factors.find((f) => f.factorType === "webauthn");
  const totpFactor = factors.find((f) => f.factorType === "totp");

  // Passkeys can't be used inside the native WebView (see isPasskeySupported),
  // so on mobile we fall back to the authenticator code.
  const canUsePasskey = isPasskeySupported() && !!passkeyFactor;

  // Prefer passkey when usable; otherwise the TOTP code.
  const [mode, setMode] = useState<"passkey" | "totp">(
    canUsePasskey ? "passkey" : "totp",
  );
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runPasskey = async () => {
    if (!passkeyFactor) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await challengePasskey(passkeyFactor.id);
      onComplete();
    } catch (e) {
      setError(
        errorMessage(e, "Passkey verification was cancelled or failed."),
      );
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value: string) => {
    if (!totpFactor || value.length !== TOTP_CODE_LENGTH) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await verifyTotp(totpFactor.id, value);
      onComplete();
    } catch (e) {
      setCode("");
      setError(errorMessage(e, "That code wasn't valid. Try again."));
    } finally {
      setBusy(false);
    }
  };

  // Dead end: the user's only factor is a passkey, but passkeys don't work
  // here (native app). They must use the web app or add an authenticator app.
  if (!totpFactor && !canUsePasskey) {
    return (
      <MfaCard
        icon={<ShieldAlert size={56} />}
        title="Can't verify on this device"
        description="Your only second factor is a passkey, which isn't supported in the app yet. Please sign in on the web, or add an authenticator app from a browser."
      >
        <div />
      </MfaCard>
    );
  }

  if (mode === "passkey" && canUsePasskey) {
    return (
      <MfaCard
        icon={<KeyRound size={56} />}
        title="Verify with your passkey"
        description="Use Face ID, Touch ID, or your security key to continue."
        error={error}
      >
        <div className="space-y-4">
          <Button onClick={runPasskey} disabled={busy} className="w-full h-12">
            {busy && <Loading className="w-5 h-5 mr-2" />}
            Continue with passkey
          </Button>
          {totpFactor && (
            <Button
              onClick={() => {
                setError(null);
                setMode("totp");
              }}
              disabled={busy}
              variant="link"
              className="w-full"
            >
              Use an authenticator code instead
            </Button>
          )}
        </div>
      </MfaCard>
    );
  }

  return (
    <MfaCard
      icon={<LockKeyhole size={56} />}
      title="Enter your authenticator code"
      description="Open your authenticator app and enter the current 6-digit code."
      error={error}
    >
      <div className="space-y-6">
        <div className="flex justify-center">
          <InputOTP
            value={code}
            onChange={(value) => {
              setCode(value);
              if (value.length === TOTP_CODE_LENGTH) {
                submitCode(value);
              }
            }}
            disabled={busy}
            autoFocus
            maxLength={TOTP_CODE_LENGTH}
          >
            <InputOTPGroup>
              {Array.from({ length: TOTP_CODE_LENGTH }).map((_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
        {busy && (
          <div className="flex justify-center">
            <Loading className="w-8 h-8" />
          </div>
        )}
        {canUsePasskey && (
          <Button
            onClick={() => {
              setError(null);
              setMode("passkey");
            }}
            disabled={busy}
            variant="link"
            className="w-full flex items-center gap-2"
          >
            <Smartphone className="h-4 w-4" />
            Use your passkey instead
          </Button>
        )}
      </div>
    </MfaCard>
  );
};
